"use client";

import {useState, useEffect} from "react";
import {getDeliveryDays} from "@/lib/delivery-time-mapping";

interface DeliveryDateDisplayProps {
    deliveryTimeSlug: string;
    className?: string;
}

interface DeliveryEstimateFullProps {
    deliveryTimeSlug: string;
}

/**
 * Returns color classes based on delivery time:
 * - Green: <= 14 days (relatively soon)
 * - Yellow/Amber: 15-30 days (more than 2 weeks, up to ~1 month)
 * - Gray: > 30 days or unknown/missing
 */
function getDeliveryColors(daysToAdd: number, hasValidSlug: boolean): {text: string, bg: string, icon: string} {
    if (!hasValidSlug || daysToAdd > 30) {
        return {
            text: 'text-slate-600',
            bg: 'bg-slate-50',
            icon: 'text-slate-500'
        }; // Gray for far out or missing
    }
    if (daysToAdd > 14) {
        return {
            text: 'text-amber-700',
            bg: 'bg-amber-50',
            icon: 'text-amber-600'
        }; // Yellow for > 2 weeks to 1 month
    }
    return {
        text: 'text-emerald-700',
        bg: 'bg-emerald-50',
        icon: 'text-emerald-600'
    }; // Green for soon (up to 2 weeks)
}

// Default colors for SSR/initial render
const defaultColors = {text: 'text-slate-600', bg: 'bg-slate-50', icon: 'text-slate-500'};

/**
 * Client Component that calculates and displays the delivery date with conditional coloring.
 * Using useEffect ensures new Date() only runs on the client after hydration.
 *
 * Colors based on delivery time:
 * - Green: Relatively soon (up to 2 weeks)
 * - Yellow/Amber: More than 2 weeks, up to 1 month
 * - Gray: Far out (> 1 month) or missing delivery info
 */
export function DeliveryDateDisplay({deliveryTimeSlug, className = ""}: DeliveryDateDisplayProps) {
    const [mounted, setMounted] = useState(false);
    const [deliveryDate, setDeliveryDate] = useState<string>('');
    const [colorClass, setColorClass] = useState(defaultColors.text);

    useEffect(() => {
        setMounted(true);
        const hasValidSlug = Boolean(deliveryTimeSlug && deliveryTimeSlug.length > 0);
        const daysToAdd = getDeliveryDays(deliveryTimeSlug);
        const now = new Date();
        const date = new Date(now.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
        const formattedDate = date.toLocaleDateString('de-DE', {day: 'numeric', month: 'long'});
        const colors = getDeliveryColors(daysToAdd, hasValidSlug);

        setDeliveryDate(formattedDate);
        setColorClass(colors.text);
    }, [deliveryTimeSlug]);

    // Show placeholder during SSR
    if (!mounted) {
        return <span className={`${defaultColors.text} ${className}`}>Wird berechnet...</span>;
    }

    return <span className={`${colorClass} ${className}`}>{deliveryDate}</span>;
}

/**
 * Full delivery estimate component with icon, background, and conditional colors.
 * Displays "Lieferung bis [date]" with appropriate color based on delivery time.
 */
export function DeliveryEstimateFull({deliveryTimeSlug}: DeliveryEstimateFullProps) {
    const [mounted, setMounted] = useState(false);
    const [deliveryDate, setDeliveryDate] = useState<string>('');
    const [colors, setColors] = useState(defaultColors);

    useEffect(() => {
        setMounted(true);
        const hasValidSlug = Boolean(deliveryTimeSlug && deliveryTimeSlug.length > 0);
        const daysToAdd = getDeliveryDays(deliveryTimeSlug);
        const now = new Date();
        const date = new Date(now.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
        const formattedDate = date.toLocaleDateString('de-DE', {day: 'numeric', month: 'long'});
        const colorSet = getDeliveryColors(daysToAdd, hasValidSlug);

        setDeliveryDate(formattedDate);
        setColors(colorSet);
    }, [deliveryTimeSlug]);

    return (
        <div className={`flex items-start gap-3 p-3 ${colors.bg} rounded-lg`}>
            <svg
                className={`w-5 h-5 ${colors.icon} mt-0.5 flex-shrink-0`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M8 2v4" />
                <path d="M16 2v4" />
                <rect width="18" height="18" x="3" y="4" rx="2" />
                <path d="M3 10h18" />
            </svg>
            <div>
                <p className={`text-sm font-medium ${colors.text}`}>
                    {mounted ? `Lieferung bis ${deliveryDate}` : 'Lieferdatum wird berechnet...'}
                </p>
                <p className={`text-xs ${colors.text} opacity-80`}>Bei Bestellung heute</p>
            </div>
        </div>
    );
}
